import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/layout/Header";
import { getServicesApi, getAddressesApi, addAddressApi, createMultiBookingApi, verifyMultiPaymentApi } from "../api/bookingApi";
import "./book-service.css";

// ─────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const TIME_SLOTS = [
  { id:"early",     icon:"🌅", label:"Early Hours",   range:[0,8]   },
  { id:"morning",   icon:"☀️", label:"08 AM – 12 PM", range:[8,12]  },
  { id:"afternoon", icon:"🌤", label:"12 PM – 04 PM", range:[12,16] },
  { id:"evening",   icon:"🌙", label:"After 04 PM",   range:[16,24] },
];

const STEPS = [
  { num:1, label:"Service"    },
  { num:2, label:"Date"       },
  { num:3, label:"Time"       },
  { num:4, label:"Location"   },
  { num:5, label:"Instructions"},
  { num:6, label:"Pay"        },
];

function buildDays() {
  const days = [], today = new Date(); today.setHours(0,0,0,0);
  for (let i = 0; i < 60; i++) {
    const d = new Date(today); d.setDate(today.getDate() + i); days.push(d);
  }
  return days;
}
function groupByMonth(days) {
  const map = {};
  days.forEach(d => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!map[key]) map[key] = { year:d.getFullYear(), month:d.getMonth(), days:[] };
    map[key].days.push(d);
  });
  return Object.values(map);
}
function slotDisabled(slot, date) {
  const now = new Date(), today = new Date(); today.setHours(0,0,0,0);
  const sel = new Date(date); sel.setHours(0,0,0,0);
  if (sel.getTime() !== today.getTime()) return false;
  return slot.range[1] <= now.getHours();
}
function firstAvailableSlot(date) {
  const now = new Date(), today = new Date(); today.setHours(0,0,0,0);
  const sel = new Date(date); sel.setHours(0,0,0,0);
  if (sel.getTime() !== today.getTime()) return TIME_SLOTS[0].id;
  const s = TIME_SLOTS.find(s => s.range[1] > now.getHours());
  return s ? s.id : null;
}
function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
const fmtPrice = p => parseFloat(p) === 0 ? "FREE" : `₹${parseFloat(p).toLocaleString("en-IN")}`;
const fmtDate  = d => `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;

// ── Step Progress Bar ─────────────────────────────────────
function StepBar({ current }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      gap:0, margin:"24px 0 32px", flexWrap:"wrap" }}>
      {STEPS.map((s, i) => {
        const done    = s.num < current;
        const active  = s.num === current;
        return (
          <React.Fragment key={s.num}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
              <div style={{
                width:38, height:38, borderRadius:"50%",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontWeight:800, fontSize:14,
                background: done ? "#16A34A" : active ? "#1F4E8C" : "#F1F5F9",
                color:      done || active ? "#fff" : "#94A3B8",
                border: active ? "3px solid #BFDBFE" : "none",
                transition:".2s"
              }}>
                {done ? "✓" : s.num}
              </div>
              <span style={{
                fontSize:11, fontWeight:700,
                color: active ? "#1F4E8C" : done ? "#16A34A" : "#94A3B8"
              }}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                height:3, width:40, marginBottom:20,
                background: done ? "#16A34A" : "#E2E8F0",
                transition:".2s"
              }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Wrapper card ──────────────────────────────────────────
function Card({ title, subtitle, children }) {
  return (
    <div style={{
      background:"#fff", borderRadius:20,
      border:"1px solid #E2E8F0",
      boxShadow:"0 4px 20px rgba(0,0,0,.06)",
      padding:"28px 28px", marginBottom:0
    }}>
      <h2 style={{ margin:"0 0 4px", fontSize:20, fontWeight:800, color:"#1E293B" }}>{title}</h2>
      {subtitle && <p style={{ margin:"0 0 20px", fontSize:14, color:"#64748B" }}>{subtitle}</p>}
      {children}
    </div>
  );
}

// ── Nav buttons ───────────────────────────────────────────
function NavBtns({ onBack, onNext, nextLabel="Next →", nextDisabled=false, loading=false }) {
  return (
    <div style={{ display:"flex", gap:12, marginTop:24 }}>
      {onBack && (
        <button onClick={onBack} style={{
          flex:1, padding:"13px", borderRadius:12,
          border:"1px solid #CBD5E1", background:"#fff",
          fontWeight:700, fontSize:14, cursor:"pointer",
          color:"#64748B", fontFamily:"inherit"
        }}>← Back</button>
      )}
      <button onClick={onNext} disabled={nextDisabled || loading} style={{
        flex:2, padding:"13px", borderRadius:12, border:"none",
        background: nextDisabled ? "#CBD5E1" : "#1F4E8C",
        color:"#fff", fontWeight:800, fontSize:15,
        cursor: nextDisabled ? "not-allowed" : "pointer",
        fontFamily:"inherit", display:"flex", alignItems:"center",
        justifyContent:"center", gap:8
      }}>
        {loading ? (
          <><span style={{ width:16,height:16,border:"2px solid rgba(255,255,255,.4)",
            borderTopColor:"#fff",borderRadius:"50%",
            display:"inline-block",animation:"bk-spin .7s linear infinite"
          }}/> Processing...</>
        ) : nextLabel}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────
export default function BookService() {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);

  // Step 1 — service
  const [allServices,     setAllServices]     = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [serviceSearch,   setServiceSearch]   = useState("");
  const [catFilter,       setCatFilter]       = useState("All");
  const [selectedServices, setSelectedServices] = useState([]);   // multi-select
  const totalAmount = selectedServices.reduce((t, s) => t + parseFloat(s.price || 0), 0);
  const isSelected  = (svc) => selectedServices.some(x => x.id === svc.id);
  const toggleService = (svc) =>
    setSelectedServices(prev =>
      prev.some(x => x.id === svc.id) ? prev.filter(x => x.id !== svc.id) : [...prev, svc]
    );
  const [notFoundNote,    setNotFoundNote]    = useState("");

  // Step 2 — date
  const allDays = buildDays();
  const months  = groupByMonth(allDays);
  const today   = allDays[0];
  const [activeMonthKey, setActiveMonthKey] = useState(`${today.getFullYear()}-${today.getMonth()}`);
  const [selectedDate,   setSelectedDate]   = useState(today);

  // Step 3 — time
  const [selectedTime, setSelectedTime] = useState(() => firstAvailableSlot(today));

  // Step 4 — location
  const [addresses,    setAddresses]    = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [newAddr,      setNewAddr]      = useState({ label:"", line1:"", city:"", pincode:"" });
  const [locating,     setLocating]     = useState(false);
  const [addrLoading,  setAddrLoading]  = useState(true);
  const [mapCoords,    setMapCoords]    = useState(null); // { lat, lon } for map preview

  // Step 5 — instructions
  const [instruction, setInstruction] = useState("");

  // Step 6 — payment
  const [paying,    setPaying]    = useState(false);
  const [bookingIds, setBookingIds] = useState([]);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState("");

  // Load services
  useEffect(() => {
    getServicesApi()
      .then(r => {
        const list = r.data.services || [];
        setAllServices(list);

        // Arrived from the Services page: resolve the clicked card to a real
        // DB service (by id, else by exact name) and skip straight to step 2.
        const wantedId   = location.state?.service_id;
        const wantedName = (location.state?.service || "").trim();
        if (wantedId || wantedName) {
          const active = list.filter(s => s.is_active);
          const match =
            active.find(s => wantedId && String(s.id) === String(wantedId)) ||
            active.find(s => wantedName && s.name.trim().toLowerCase() === wantedName.toLowerCase());
          if (match) {
            setSelectedServices([match]);
          } else if (wantedName) {
            setServiceSearch(wantedName);
            setNotFoundNote(`"${wantedName}" isn't available for online booking yet. Pick the closest service below, or call us and we'll arrange it.`);
          }
        }
      })
      .catch(()=>{})
      .finally(()=>setServicesLoading(false));
  }, []);

  // Load saved addresses when reaching step 4
  useEffect(() => {
    if (step !== 4) return;
    setAddrLoading(true);
    getAddressesApi()
      .then(r => {
        const saved = r.data.addresses || [];
        setAddresses(saved);
        if (saved.length > 0 && !selectedAddr) setSelectedAddr(saved[0].id);
      })
      .catch(()=>{})
      .finally(()=>setAddrLoading(false));
  }, [step]);

  // GPS — reverse geocode with fallback
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported by your browser.");
    setLocating(true);
    setShowAddrForm(true);

    navigator.geolocation.getCurrentPosition(
      async pos => {
        const { latitude, longitude } = pos.coords;
        // Always set map immediately with exact GPS coords
        setMapCoords({ lat: latitude, lon: longitude });
        try {
          // Use Nominatim with zoom=18 for street-level detail
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1&zoom=18&accept-language=en`,
            { headers: { "User-Agent": "SuvidhaElderCare/1.0 contact@suvidhacare.in" } }
          );
          const data = await res.json();
          const a    = data.address || {};

          // Build detailed street address
          const line1Parts = [
            a.house_number,
            a.road || a.pedestrian || a.footway || a.street || a.path,
            a.neighbourhood || a.quarter || a.suburb,
            a.village || a.hamlet,
          ].filter(Boolean);

          const line1   = line1Parts.length > 0
            ? line1Parts.join(", ")
            : (data.display_name || "").split(",").slice(0, 3).join(", ").trim();

          const city    = a.city || a.town || a.village || a.municipality || a.county || "";
          const state   = a.state || "";
          const pincode = a.postcode || "";

          setNewAddr({
            label:   "Current Location",
            line1:   line1 || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            city:    city + (state && city && city !== state ? `, ${state}` : state),
            pincode: pincode,
          });

        } catch (e) {
          // Fallback: BigDataCloud
          try {
            const r2 = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const d2 = await r2.json();
            const locality = d2.locality || d2.city || "";
            const city     = d2.city || d2.principalSubdivision || "";
            const state    = d2.principalSubdivision || "";
            setNewAddr({
              label:   "Current Location",
              line1:   locality || `Near (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
              city:    city + (state && city !== state ? `, ${state}` : ""),
              pincode: d2.postcode || "",
            });
          } catch {
            setNewAddr({
              label:   "Current Location",
              line1:   `Near (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`,
              city:    "",
              pincode: "",
            });
          }
        } finally {
          setLocating(false);
        }
      },
      err => {
        setLocating(false);
        setShowAddrForm(false);
        if (err.code === 1)
          alert("📍 Location access denied.\n\nTo fix:\n1. Click the 🔒 lock icon in the browser address bar\n2. Set Location to Allow\n3. Refresh and try again");
        else if (err.code === 2)
          alert("Could not detect your location. Please check GPS/network and try again.");
        else
          alert("Location request timed out. Please enter your address manually.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSaveAddress = async () => {
    if (!newAddr.line1||!newAddr.city) return alert("Address line and city are required.");
    try {
      const res  = await addAddressApi({ label:newAddr.label||"Home", line1:newAddr.line1, city:newAddr.city, pincode:newAddr.pincode });
      const saved = res.data;
      const newEntry = { id: saved.id||Date.now(), label:newAddr.label||"Home", line1:newAddr.line1, city:newAddr.city, pincode:newAddr.pincode };
      setAddresses(p=>[...p, newEntry]);
      setSelectedAddr(newEntry.id);
      setShowAddrForm(false);
      setNewAddr({ label:"", line1:"", city:"", pincode:"" });
    } catch {
      // fallback: save locally
      const local = { id:Date.now(), label:newAddr.label||"Home", line1:newAddr.line1, city:newAddr.city, pincode:newAddr.pincode };
      setAddresses(p=>[...p, local]);
      setSelectedAddr(local.id);
      setShowAddrForm(false);
      setNewAddr({ label:"", line1:"", city:"", pincode:"" });
    }
  };

  // ── PAYMENT ──────────────────────────────────────────────
  const handlePay = async () => {
    setPaying(true); setError("");
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Failed to load payment gateway. Check your internet.");

      const user = (() => { try { return JSON.parse(localStorage.getItem("user")||"{}"); } catch { return {}; } })();
      const addr = addresses.find(a => a.id === selectedAddr);

      // One booking per selected service, one combined payment
      const bkRes = await createMultiBookingApi({
        service_ids:  selectedServices.map(x => x.id),
        address_id:   selectedAddr || null,
        booking_date: selectedDate.toISOString().split("T")[0],
        time_slot:    selectedTime,
        instructions: instruction,
      });
      const bkData = bkRes.data;

      // Everything free — skip payment
      if (bkData.free) {
        setBookingIds(bkData.booking_ids);
        setSuccess(true);
        setPaying(false);
        return;
      }

      // Paid — open Razorpay for the combined total
      const rzp = new window.Razorpay({
        key:         bkData.key_id,
        amount:      Math.round(parseFloat(bkData.amount) * 100),
        currency:    bkData.currency || "INR",
        name:        "Suvidha Elder Care",
        description: selectedServices.length === 1 ? selectedServices[0].name : `${selectedServices.length} services`,
        order_id:    bkData.razorpay_order_id,
        prefill: {
          name:    user.full_name || "",
          contact: user.phone    || "",
        },
        theme: { color: "#1F4E8C" },
        modal: { ondismiss: () => setPaying(false) },
        handler: async response => {
          try {
            await verifyMultiPaymentApi({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            });
            setBookingIds(bkData.booking_ids);
            setSuccess(true);
          } catch(e) {
            setError("Payment verified but booking failed. Contact support.");
          } finally {
            setPaying(false);
          }
        },
      });
      rzp.open();
    } catch(e) {
      setError(e?.response?.data?.message || e.message || "Something went wrong.");
      setPaying(false);
    }
  };

  // ── SUCCESS SCREEN ────────────────────────────────────────
  if (success) {
    return (
      <>
        <Header />
        <div className="bk-page">
          <div style={{ maxWidth:500, margin:"60px auto", textAlign:"center", padding:"0 20px" }}>
            <div style={{ fontSize:72, marginBottom:16 }}>🎉</div>
            <h2 style={{ fontSize:26, fontWeight:900, color:"#1E293B", margin:"0 0 10px" }}>
              Booking Confirmed!
            </h2>
            <p style={{ color:"#64748B", fontSize:15, lineHeight:1.6, marginBottom:24 }}>
              Your <strong>{selectedServices.map(x=>x.name).join(", ")}</strong> {selectedServices.length>1?"have":"has"} been booked for{" "}
              <strong>{fmtDate(selectedDate)}</strong> —{" "}
              <strong>{TIME_SLOTS.find(s=>s.id===selectedTime)?.label}</strong>.
              <br/>Our team will confirm your booking shortly.
            </p>
            {bookingIds.length > 0 && (
              <div style={{ background:"#F0FDF4", border:"1px solid #A7F3D0", borderRadius:12, padding:"12px 20px", marginBottom:20, fontSize:14 }}>
                Booking ID{bookingIds.length>1?"s":""}: <strong>{bookingIds.map(id=>"#"+String(id).padStart(5,"0")).join(", ")}</strong>
              </div>
            )}
            <div style={{ display:"flex", gap:12 }}>
              <button onClick={() => navigate("/my-bookings")} style={{
                flex:1, padding:"13px", borderRadius:12, border:"none",
                background:"#1F4E8C", color:"#fff", fontWeight:800,
                fontSize:14, cursor:"pointer", fontFamily:"inherit"
              }}>View My Bookings</button>
              <button onClick={() => { setStep(1); setSelectedServices([]); setSuccess(false); setBookingIds([]); }} style={{
                flex:1, padding:"13px", borderRadius:12,
                border:"1px solid #CBD5E1", background:"#fff",
                fontWeight:700, fontSize:14, cursor:"pointer",
                color:"#64748B", fontFamily:"inherit"
              }}>Book Another</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── STEP SCREENS ──────────────────────────────────────────
  const categories = ["All", ...new Set(allServices.map(s=>s.category))];
  const filtered   = allServices.filter(s => {
    const mCat = catFilter==="All" || s.category===catFilter;
    const mSrc = !serviceSearch || s.name.toLowerCase().includes(serviceSearch.toLowerCase()) || (s.description||"").toLowerCase().includes(serviceSearch.toLowerCase());
    return mCat && mSrc && s.is_active;
  });

  const visibleDays = months.find(m=>`${m.year}-${m.month}`===activeMonthKey)?.days || [];
  const addrObj     = addresses.find(a=>a.id===selectedAddr);

  return (
    <>
      <Header />
      <div className="bk-page">
        <div style={{ maxWidth:720, margin:"0 auto", padding:"0 20px 60px" }}>

          {/* Step bar */}
          <StepBar current={step} />

          {/* ── STEP 1: SELECT SERVICE ── */}
          {step === 1 && (
            <Card title="Select a Service" subtitle="Choose the service you need">
              {notFoundNote && (
                <div role="status" style={{ background:"#FFF7ED", border:"1px solid #FED7AA", color:"#9A3412", padding:"10px 14px", borderRadius:10, fontSize:13.5, marginBottom:12, lineHeight:1.5 }}>
                  {notFoundNote}
                </div>
              )}
              <input value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)}
                placeholder="🔍 Search services..." style={{
                  width:"100%", padding:"11px 14px", borderRadius:10,
                  border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box",
                  marginBottom:12, outline:"none", fontFamily:"inherit"
                }}
              />
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
                {categories.map(cat => (
                  <button key={cat} onClick={()=>setCatFilter(cat)} style={{
                    padding:"6px 16px", borderRadius:999, border:"none",
                    cursor:"pointer", fontWeight:700, fontSize:12,
                    background: catFilter===cat ? "#1F4E8C" : "#F1F5F9",
                    color:      catFilter===cat ? "#fff"    : "#64748B",
                    fontFamily:"inherit"
                  }}>{cat}</button>
                ))}
              </div>
              {servicesLoading ? (
                <div style={{ textAlign:"center", padding:40, color:"#94A3B8" }}>Loading services...</div>
              ) : filtered.length===0 ? (
                <div style={{ textAlign:"center", padding:40, color:"#94A3B8" }}>No services found.</div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14 }}>
                  {filtered.map(svc => (
                    <div key={svc.id} onClick={()=>toggleService(svc)}
                      role="checkbox" aria-checked={isSelected(svc)} tabIndex={0}
                      onKeyDown={e=>{ if (e.key===" "||e.key==="Enter") { e.preventDefault(); toggleService(svc); } }}
                      style={{ background: isSelected(svc) ? "#EFF6FF" : "#F8FAFC", borderRadius:16,
                        border: isSelected(svc) ? "2px solid #1F4E8C" : "2px solid #E2E8F0",
                        padding:"18px", cursor:"pointer", transition:"all .2s", display:"flex", flexDirection:"column", position:"relative" }}>
                      <span aria-hidden="true" style={{ position:"absolute", top:14, right:14, width:22, height:22, borderRadius:7,
                        border: isSelected(svc) ? "none" : "2px solid #CBD5E1",
                        background: isSelected(svc) ? "#1F4E8C" : "#fff", color:"#fff",
                        display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:900 }}>
                        {isSelected(svc) ? "✓" : ""}
                      </span>
                      <span style={{ fontSize:11, fontWeight:800, padding:"2px 8px", borderRadius:999,
                        background:"#EFF6FF", color:"#1D4ED8", alignSelf:"flex-start", marginBottom:10 }}>
                        {svc.category}
                      </span>
                      <div style={{ fontWeight:800, fontSize:15, color:"#1E293B", marginBottom:6 }}>{svc.name}</div>
                      {svc.description && (
                        <div style={{ fontSize:12, color:"#64748B", lineHeight:1.5, marginBottom:10,
                          flex:1, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                          {svc.description}
                        </div>
                      )}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:"auto" }}>
                        <span style={{ fontSize:20, fontWeight:900, color: parseFloat(svc.price)===0?"#16A34A":"#1F4E8C" }}>
                          {fmtPrice(svc.price)}
                        </span>
                        {svc.duration && <span style={{ fontSize:11, color:"#94A3B8" }}>⏱ {svc.duration}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ position:"sticky", bottom:12, marginTop:20, background:"#fff", border:"1px solid #E2E8F0",
                borderRadius:14, padding:"12px 14px", boxShadow:"0 6px 20px rgba(15,23,42,.08)",
                display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:150 }}>
                  <div style={{ fontSize:13, color:"#64748B", fontWeight:600 }}>
                    {selectedServices.length === 0 ? "Tick one or more services" : `${selectedServices.length} selected`}
                  </div>
                  {selectedServices.length > 0 && (
                    <div style={{ fontSize:18, fontWeight:900, color:"#1F4E8C" }}>{fmtPrice(totalAmount)}</div>
                  )}
                </div>
                {selectedServices.length > 0 && (
                  <button onClick={()=>setSelectedServices([])} style={{ border:"none", background:"none", color:"#64748B",
                    fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit", textDecoration:"underline" }}>Clear</button>
                )}
                <button disabled={selectedServices.length===0} onClick={()=>setStep(2)} style={{
                  padding:"12px 22px", borderRadius:12, border:"none", fontWeight:800, fontSize:14, fontFamily:"inherit",
                  background: selectedServices.length ? "#1F4E8C" : "#CBD5E1", color:"#fff",
                  cursor: selectedServices.length ? "pointer" : "not-allowed" }}>
                  Continue →
                </button>
              </div>
            </Card>
          )}

          {/* ── STEP 2: SELECT DATE ── */}
          {step === 2 && (
            <Card title="📅 Choose a Date" subtitle="Pick the date for your service">
              <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
                {months.map(m => {
                  const key = `${m.year}-${m.month}`;
                  return (
                    <button key={key} onClick={()=>setActiveMonthKey(key)} style={{
                      padding:"7px 16px", borderRadius:20, border:"none", cursor:"pointer",
                      fontWeight:700, fontSize:13,
                      background: activeMonthKey===key ? "#1F4E8C" : "#F1F5F9",
                      color:      activeMonthKey===key ? "#fff"    : "#64748B",
                      fontFamily:"inherit"
                    }}>{MONTHS_FULL[m.month]}</button>
                  );
                })}
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
                {visibleDays.map(d => {
                  const isSel   = selectedDate.toDateString()===d.toDateString();
                  const isToday = today.toDateString()===d.toDateString();
                  return (
                    <button key={d.toDateString()} onClick={()=>{setSelectedDate(d);setSelectedTime(firstAvailableSlot(d));}}
                      style={{
                        width:58, padding:"10px 4px", borderRadius:12, border:"none",
                        cursor:"pointer", display:"flex", flexDirection:"column",
                        alignItems:"center", gap:4,
                        background: isSel ? "#1F4E8C" : isToday ? "#EFF6FF" : "#F8FAFC",
                        color:      isSel ? "#fff"    : "#1E293B",
                        fontWeight: isSel ? 800 : 600,
                        outline: isToday && !isSel ? "2px solid #1F4E8C" : "none",
                        fontFamily:"inherit"
                      }}>
                      <span style={{ fontSize:16, fontWeight:800 }}>{d.getDate()}</span>
                      <span style={{ fontSize:11 }}>{DAYS[d.getDay()]}</span>
                      {isToday && <span style={{ width:5, height:5, borderRadius:"50%", background: isSel?"#93C5FD":"#1F4E8C" }}/>}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize:13, color:"#64748B", background:"#F8FAFC", padding:"10px 14px", borderRadius:10 }}>
                📅 Selected: <strong>{fmtDate(selectedDate)}</strong>
              </div>
              <NavBtns onBack={()=>setStep(1)} onNext={()=>setStep(3)} nextLabel="Next: Choose Time →" />
            </Card>
          )}

          {/* ── STEP 3: SELECT TIME ── */}
          {step === 3 && (
            <Card title="🕐 Choose a Time Slot" subtitle="Select the best time for you">
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                {TIME_SLOTS.map(slot => {
                  const dis    = slotDisabled(slot, selectedDate);
                  const active = selectedTime===slot.id;
                  return (
                    <button key={slot.id} onClick={()=>!dis&&setSelectedTime(slot.id)} disabled={dis}
                      style={{
                        padding:"18px", borderRadius:14, border:"2px solid",
                        borderColor: active ? "#1F4E8C" : "#E2E8F0",
                        background:  active ? "#EFF6FF" : dis ? "#F8FAFC" : "#fff",
                        cursor:      dis ? "not-allowed" : "pointer",
                        opacity:     dis ? 0.45 : 1,
                        display:"flex", flexDirection:"column", alignItems:"center", gap:6,
                        fontFamily:"inherit"
                      }}>
                      <span style={{ fontSize:28 }}>{slot.icon}</span>
                      <span style={{ fontWeight:800, fontSize:14, color: active?"#1F4E8C":"#1E293B" }}>{slot.label}</span>
                      {dis && <span style={{ fontSize:11, color:"#EF4444", fontWeight:700 }}>Passed</span>}
                      {active && <span style={{ fontSize:11, color:"#1F4E8C", fontWeight:700 }}>✓ Selected</span>}
                    </button>
                  );
                })}
              </div>
              {!selectedTime && (
                <div style={{ color:"#EF4444", fontSize:13, marginBottom:12 }}>
                  ⚠ No slots available for today. Please go back and pick a future date.
                </div>
              )}
              <NavBtns onBack={()=>setStep(2)} onNext={()=>setStep(4)} nextLabel="Next: Add Location →" nextDisabled={!selectedTime} />
            </Card>
          )}

          {/* ── STEP 4: LOCATION ── */}
          {step === 4 && (
            <Card title="📍 Service Location" subtitle="Where should we send the caregiver?">
              {addrLoading ? (
                <div style={{ padding:20, color:"#94A3B8" }}>Loading saved addresses...</div>
              ) : (
                <>
                  {/* Saved addresses */}
                  {addresses.map(addr => (
                    <div key={addr.id} onClick={async () => {
                      setSelectedAddr(addr.id);
                      // Geocode address to show on map
                      try {
                        const q   = encodeURIComponent(`${addr.line1}, ${addr.city}, India`);
                        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, { headers:{"User-Agent":"SuvidhaElderCare/1.0"} });
                        const geo = await res.json();
                        if (geo[0]) setMapCoords({ lat: parseFloat(geo[0].lat), lon: parseFloat(geo[0].lon) });
                      } catch { /* map won't show, not critical */ }
                    }} style={{
                      display:"flex", alignItems:"center", gap:14,
                      padding:"14px 16px", borderRadius:14, marginBottom:10,
                      border:"2px solid " + (selectedAddr===addr.id ? "#1F4E8C" : "#E2E8F0"),
                      background: selectedAddr===addr.id ? "#EFF6FF" : "#F8FAFC",
                      cursor:"pointer"
                    }}>
                      <span style={{ fontSize:22 }}>📍</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:800, fontSize:14, color:"#1E293B" }}>{addr.label}</div>
                        <div style={{ fontSize:13, color:"#64748B" }}>{addr.line1}, {addr.city} {addr.pincode}</div>
                      </div>
                      {selectedAddr===addr.id && (
                        <span style={{ width:22, height:22, borderRadius:"50%", background:"#1F4E8C",
                          color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:13, fontWeight:900 }}>✓</span>
                      )}
                    </div>
                  ))}

                  {/* Map Preview */}
                  {mapCoords && (
                    <div style={{ borderRadius:14, overflow:"hidden", marginBottom:14, border:"2px solid #1F4E8C", position:"relative" }}>
                      <div style={{ position:"absolute", top:10, left:10, zIndex:1,
                        background:"#1F4E8C", color:"#fff", fontSize:11, fontWeight:700,
                        padding:"3px 10px", borderRadius:999 }}>
                        📍 Your Location
                      </div>
                      <iframe
                        title="location-map"
                        width="100%"
                        height="220"
                        style={{ border:0, display:"block" }}
                        loading="lazy"
                        allowFullScreen
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lon-0.002},${mapCoords.lat-0.002},${mapCoords.lon+0.002},${mapCoords.lat+0.002}&layer=mapnik&marker=${mapCoords.lat},${mapCoords.lon}`}
                      />
                      <div style={{ padding:"10px 14px", background:"#EFF6FF", fontSize:13, color:"#1E40AF", fontWeight:600 }}>
                        📌 {newAddr.line1}{newAddr.city ? `, ${newAddr.city}` : ""}{newAddr.pincode ? ` - ${newAddr.pincode}` : ""}
                      </div>
                    </div>
                  )}

                  {/* Add buttons */}
                  {!showAddrForm && (
                    <div style={{ display:"flex", gap:10, marginTop:10, flexWrap:"wrap" }}>
                      <button onClick={handleUseCurrentLocation} disabled={locating} style={{
                        flex:1, padding:"13px", borderRadius:12,
                        border:"2px dashed " + (locating?"#93C5FD":"#16A34A"),
                        background: locating?"#EFF6FF":"#F0FDF4",
                        color: locating?"#3B82F6":"#16A34A",
                        fontWeight:700, fontSize:14, cursor: locating?"not-allowed":"pointer",
                        fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8
                      }}>
                        {locating ? (
                          <><span style={{ width:14,height:14,border:"2px solid #93C5FD",borderTopColor:"#3B82F6",
                            borderRadius:"50%",display:"inline-block",animation:"bk-spin .7s linear infinite" }}/>
                          Fetching location...</>
                        ) : <><span style={{fontSize:18}}>📍</span> Use Current Location</>}
                      </button>
                      <button onClick={()=>setShowAddrForm(true)} style={{
                        flex:1, padding:"13px", borderRadius:12,
                        border:"2px dashed #CBD5E1", background:"#F8FAFC",
                        color:"#64748B", fontWeight:700, fontSize:14,
                        cursor:"pointer", fontFamily:"inherit"
                      }}>＋ Add Manually</button>
                    </div>
                  )}

                  {/* Address form */}
                  {showAddrForm && (
                    <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:14, padding:20, marginTop:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                        <strong style={{ fontSize:15 }}>New Address</strong>
                        {newAddr.label==="Current Location" && (
                          <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:999, background:"#D1FAE5", color:"#059669" }}>
                            📍 Auto-filled from GPS
                          </span>
                        )}
                      </div>
                      {[
                        { key:"label",   label:"Label",         placeholder:"Home / Office" },
                        { key:"line1",   label:"Address Line *", placeholder:"Flat / Street / Area" },
                        { key:"city",    label:"City *",         placeholder:"Visakhapatnam" },
                        { key:"pincode", label:"Pincode",        placeholder:"530001", numeric:true, max:6 },
                      ].map(f => (
                        <div key={f.key} style={{ marginBottom:12 }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>{f.label}</label>
                          <input value={newAddr[f.key]} placeholder={f.placeholder}
                            maxLength={f.max} inputMode={f.numeric?"numeric":undefined}
                            onChange={e=>setNewAddr(p=>({...p,[f.key]:f.numeric?e.target.value.replace(/\D/g,""):e.target.value}))}
                            style={{ width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #CBD5E1",
                              fontSize:14, boxSizing:"border-box", outline:"none", fontFamily:"inherit" }}
                          />
                        </div>
                      ))}
                      <div style={{ display:"flex", gap:10 }}>
                        <button onClick={handleSaveAddress} style={{
                          flex:2, padding:"11px", borderRadius:10, border:"none",
                          background:"#1F4E8C", color:"#fff", fontWeight:800,
                          fontSize:14, cursor:"pointer", fontFamily:"inherit"
                        }}>Save Address</button>
                        <button onClick={()=>{setShowAddrForm(false);setNewAddr({label:"",line1:"",city:"",pincode:""});setMapCoords(null); }} style={{
                          flex:1, padding:"11px", borderRadius:10,
                          border:"1px solid #CBD5E1", background:"#fff",
                          fontWeight:700, fontSize:14, cursor:"pointer",
                          color:"#64748B", fontFamily:"inherit"
                        }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </>
              )}
              <NavBtns onBack={()=>setStep(3)} onNext={()=>setStep(5)} nextLabel="Next: Add Instructions →" />
            </Card>
          )}

          {/* ── STEP 5: INSTRUCTIONS ── */}
          {step === 5 && (
            <Card title="📝 Special Instructions" subtitle="Anything we should know? (Optional)">
              <textarea rows={5} value={instruction} onChange={e=>setInstruction(e.target.value)}
                placeholder="e.g. Patient uses a wheelchair. Please bring extra gloves. Door code is 1234..."
                style={{
                  width:"100%", padding:"14px", borderRadius:12,
                  border:"1px solid #E2E8F0", fontSize:14, resize:"vertical",
                  fontFamily:"inherit", outline:"none", boxSizing:"border-box",
                  lineHeight:1.6
                }}
              />
              <div style={{ fontSize:13, color:"#94A3B8", marginTop:8 }}>
                Our caregivers will do their best to follow your instructions.
              </div>
              <NavBtns onBack={()=>setStep(4)} onNext={()=>setStep(6)} nextLabel="Review & Pay →" />
            </Card>
          )}

          {/* ── STEP 6: CONFIRM & PAY ── */}
          {step === 6 && (
            <Card title="✅ Review & Confirm" subtitle="Check your booking details before paying">

              {/* Summary table with Edit buttons */}
              <div style={{ borderRadius:14, overflow:"hidden", marginBottom:20, border:"1px solid #E2E8F0" }}>
                {[
                  { label: selectedServices.length>1 ? "Services" : "Service", editStep:1,
                    value: selectedServices.map(x=>`${x.name} — ${fmtPrice(x.price)}`).join("\n"), bold:true },
                  { label:"Date",         editStep:2, value:fmtDate(selectedDate) },
                  { label:"Time",         editStep:3, value:TIME_SLOTS.find(s=>s.id===selectedTime)?.label },
                  { label:"Location",     editStep:4, value:addrObj?`${addrObj.line1}, ${addrObj.city}`:"Not selected", warn:!addrObj },
                  { label:"Instructions", editStep:5, value:instruction||"None", muted:!instruction },
                  { label:"Amount",       editStep:null, value:fmtPrice(totalAmount), highlight:true },
                ].map(({ label, editStep, value, sub, bold, highlight, warn, muted }) => (
                  <div key={label} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"13px 18px", borderBottom:"1px solid #E2E8F0",
                    background: highlight ? "#EFF6FF" : "#fff",
                  }}>
                    {/* Label + value */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:".4px", marginBottom:2 }}>
                        {label}
                      </div>
                      <div style={{ fontSize:14, fontWeight: bold||highlight ? 800 : 600,
                        color: highlight?"#1F4E8C":warn?"#EF4444":muted?"#94A3B8":"#1E293B" }}>
                        <span style={{ whiteSpace:"pre-line" }}>{value}</span>
                      </div>
                      {sub && <div style={{ fontSize:12, color:"#64748B", marginTop:1 }}>{sub}</div>}
                    </div>
                    {/* Edit button */}
                    {editStep && (
                      <button onClick={() => setStep(editStep)} style={{
                        marginLeft:12, padding:"5px 14px", borderRadius:20,
                        border:"1px solid #CBD5E1", background:"#F8FAFC",
                        fontSize:12, fontWeight:700, color:"#475569",
                        cursor:"pointer", fontFamily:"inherit", flexShrink:0,
                        display:"flex", alignItems:"center", gap:4
                      }}>
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Free service note */}
              {totalAmount === 0 && (
                <div style={{ background:"#F0FDF4", border:"1px solid #A7F3D0", borderRadius:12,
                  padding:"12px 16px", marginBottom:16, fontSize:13, color:"#15803D", fontWeight:600 }}>
                  🎉 {selectedServices.length>1 ? "These are FREE services" : "This is a FREE service"} — no payment required!
                </div>
              )}

              {/* Paid service note */}
              {totalAmount > 0 && (
                <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:12,
                  padding:"12px 16px", marginBottom:16, fontSize:13, color:"#1E40AF" }}>
                  🔐 Secure payment via <strong>Razorpay</strong>. Your booking will be confirmed after payment.
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10,
                  padding:"12px 14px", marginBottom:16, color:"#DC2626", fontSize:13 }}>
                  ⚠️ {error}
                </div>
              )}

              <NavBtns
                onBack={()=>setStep(5)}
                onNext={handlePay}
                nextLabel={totalAmount===0 ? "✅ Confirm Booking" : `Pay ${fmtPrice(totalAmount)} →`}
                loading={paying}
              />
            </Card>
          )}

        </div>
      </div>
      <style>{`@keyframes bk-spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}